import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../ui/dialog';
import {
  Target,
  BarChart3,
  Megaphone,
  Rocket,
  LineChart,
  Gauge,
  Users,
  Laptop2,
  Zap,
  ArrowRight,
  ExternalLink,
  CalendarDays,
  CheckCircle2,
  Check,
  X,
  ChevronUp,
  BookOpen,
  HelpCircle,
  Mail,
  MessageSquare,
  Home,
  Menu as MenuIcon,
  Settings,
  Search,
  Star,
  Info,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Globe,
  Server,
  Cpu,
  Database,
  Cloud,
  Wifi,
  Activity,
  Terminal,
  Code,
  Lock,
  Shield,
  ShieldCheck,
  Key,
  BadgeCheck,
  Phone,
  MessageCircle,
  Send,
  Bell,
  Bot,
  ShoppingCart,
  Package,
  Truck,
  CreditCard,
  DollarSign,
  Gift,
  MonitorSmartphone,
  Palette,
  Image as ImageIcon,
  Camera,
  Video,
  Mic,
  Lightbulb,
  FileText,
  File,
  Folder,
  Copy,
  Paperclip,
  Briefcase,
  Calendar,
  Clock,
  Tag,
  Bookmark,
  Flag,
  GraduationCap,
  TrendingUp,
  Award,
  Crown,
  Share2,
  Heart,
  ChevronDown,
  ChevronRight,
  Download,
  Upload,
  RefreshCw,
  Repeat,
  MapPin,
  Layers,
  Layout,
  Grid,
  List,
  Filter,
} from 'lucide-react';
import { apiUrl } from '../../utils/api';
import { API_URLS } from '../../config/api';
import { formatPriceWithoutCents } from '../../utils/price';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import MediaDisplay from '../MediaDisplay';

const normalize = (s) => (s || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/ı/g,'i')
  .replace(/i̇/g,'i')
  .replace(/ş/g,'s')
  .replace(/ğ/g,'g')
  .replace(/ç/g,'c')
  .replace(/ö/g,'o')
  .replace(/ü/g,'u')
  .replace(/\s+/g, ' ');

const matchesReklam = (val) => {
  const n = normalize(val);
  return n === 'reklam' || 
         n === 'pazarlama' ||
         n === 'reklam-hizmetleri' ||
         n === 'reklam hizmetleri' ||
         n === 'dijital pazarlama' ||
         n === 'dijital-pazarlama' ||
         n.includes('reklam') || 
         n.includes('pazarlama');
};

// Icon mapping for channel highlights
const iconMap = {
  BarChart3: <BarChart3 className="w-10 h-10 text-rose-600" />,
  Megaphone: <Megaphone className="w-10 h-10 text-rose-600" />,
  Users: <Users className="w-10 h-10 text-rose-600" />,
  Target: <Target className="w-10 h-10 text-rose-600" />,
};

// Icon mapping for packages - tüm ikonları desteklemek için
const packageIconMap = {
  // Temel İkonlar
  Home,
  Menu: MenuIcon,
  Settings,
  Search,
  Star,
  
  // Bilgi & Yardım
  HelpCircle,
  Info,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  
  // Teknoloji & Hosting
  Globe,
  Server,
  Cpu,
  Database,
  Cloud,
  Wifi,
  Activity,
  Terminal,
  Code,
  
  // Güvenlik
  Lock,
  Shield,
  ShieldCheck,
  Key,
  BadgeCheck,
  
  // İletişim
  Mail,
  Phone,
  MessageCircle,
  MessageSquare,
  Send,
  Bell,
  Bot,
  
  // E-ticaret & Finans
  ShoppingCart,
  Package,
  Truck,
  CreditCard,
  DollarSign,
  Gift,
  
  // Tasarım & Medya
  MonitorSmartphone,
  Palette,
  Image: ImageIcon,
  Camera,
  Video,
  Mic,
  Lightbulb,
  
  // İçerik & Belgeler
  FileText,
  BookOpen,
  File,
  Folder,
  Copy,
  Paperclip,
  
  // İş & Organizasyon
  Briefcase,
  Users,
  Calendar,
  Clock,
  Tag,
  Bookmark,
  Flag,
  
  // Eğitim & Büyüme
  GraduationCap,
  Target,
  Rocket,
  TrendingUp,
  BarChart: BarChart3,
  Award,
  Crown,
  LineChart,
  
  // Pazarlama & Sosyal
  Megaphone,
  Share2,
  Heart,
  Zap,
  
  // Navigasyon & Aksiyon
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Download,
  Upload,
  RefreshCw,
  Repeat,
  
  // Görünüm & Düzen
  MapPin,
  Layers,
  Layout,
  Grid,
  List,
  Filter,
};

const whatsappNumber = '905324404011';
const whatsappMessage = encodeURIComponent('Merhaba, reklam yönetimi hizmetleriniz hakkında bilgi almak istiyorum.');
const whatsappLink = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${whatsappMessage}`;

export default function AdvertisingServicesPage() {
  const [ready, setReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [comparisonData, setComparisonData] = useState({ categories: [], packages: [], values: [] });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [offerSubmitted, setOfferSubmitted] = useState(false);
  const [offerForm, setOfferForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    company_name: '',
    project_description: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmitOfferRequest = async (e) => {
    e.preventDefault();
    if (!selectedPackage) return;

    if (!offerForm.customer_name || !offerForm.customer_email || !offerForm.customer_phone) {
      alert('Lütfen ad, e-posta ve telefon bilgilerinizi doldurun.');
      return;
    }

    setOfferSubmitting(true);
    try {
      const payload = {
        service_id: `ads_package_${selectedPackage.id ?? ''}`,
        service_title: `Reklam Paketi - ${selectedPackage.name || ''}`,
        customer_name: offerForm.customer_name,
        customer_email: offerForm.customer_email,
        customer_phone: offerForm.customer_phone,
        company_name: offerForm.company_name || null,
        project_description: offerForm.project_description || 'Reklam paketi için özel teklif talebi',
        budget_range: null,
        timeline: null,
        additional_requirements: JSON.stringify({
          source: 'advertising_services',
          package_id: selectedPackage.id ?? null,
          package_name: selectedPackage.name || null,
          package_slug: selectedPackage.slug || null,
          package_url: selectedPackage.url || null
        })
      };

      const res = await fetch(API_URLS.QUOTE_REQUESTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error('Teklif isteği gönderilemedi');
      }

      setOfferSubmitted(true);
      setOfferForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        company_name: '',
        project_description: ''
      });

      setTimeout(() => {
        setOfferSubmitted(false);
        setOfferDialogOpen(false);
        setSelectedPackage(null);
      }, 1500);
    } catch (error) {
      console.error('Error submitting advertising offer request:', error);
      alert('Teklif talebiniz gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setOfferSubmitting(false);
    }
  };

  const fetchComparisonData = async () => {
    try {
      const response = await fetch(API_URLS.ADVERTISING_PACKAGE_COMPARISON);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setComparisonData(result.data);
      }
    } catch (error) {
      console.error('Error fetching comparison data:', error);
    }
  };

  // Helper function to get feature value for a package
  const getFeatureValue = (packageId, featureId) => {
    const value = comparisonData.values?.find(
      v => v.package_id == packageId && v.feature_id == featureId
    );
    return value || { value_type: 'cross', value_text: null };
  };

  // Render feature value icon/text
  const renderFeatureValue = (valueType, valueText) => {
    switch (valueType) {
      case 'check':
        return <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />;
      case 'cross':
        return <X className="w-5 h-5 text-red-500 mx-auto" />;
      case 'optional':
        return <span className="text-xs text-gray-500 mx-auto">Opsiyonel</span>;
      case 'text':
        return <span className="text-xs text-gray-700 mx-auto">{valueText || '-'}</span>;
      default:
        return <X className="w-5 h-5 text-gray-400 mx-auto" />;
    }
  };

  useEffect(() => {
    fetchComparisonData();
  }, []);

  useEffect(() => {
    // Fetch page content from database
    fetch(apiUrl('/backend/admin_api.php?action=getPageContent&slug=reklam-hizmetleri'))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.text();
      })
      .then((text) => {
        try {
          const response = JSON.parse(text);
          if (response.status === 'success' && response.data) {
            setPageContent(response.data);
          } else {
            console.error('Page content fetch failed:', response.message || 'Unknown error');
          }
        } catch (e) {
          console.error('Error parsing page content JSON:', e);
          console.error('Response text:', text);
        }
      })
      .catch((err) => console.error('Error fetching page content:', err));

    // Fetch advertising packages from database
    fetch(apiUrl('/backend/api.php?action=getAdvertisingPackages'))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.text();
      })
      .then((text) => {
        try {
          const data = JSON.parse(text);
          const packagesArray = Array.isArray(data) ? data : (data.packages || data.data || []);
          setPackages(packagesArray);
        } catch (e) {
          console.error('Error parsing packages JSON:', e);
          console.error('Response text:', text);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching packages:', err);
        setLoading(false);
      });

    // Fetch blogs from "reklam" category
    const fetchBlogs = async () => {
      try {
        // Try server-side filtered fetch first with different category slug variations
        const categorySlugs = ['reklam', 'pazarlama', 'reklam-hizmetleri'];
        let list = [];
        
        for (const slug of categorySlugs) {
          const baseUrl = apiUrl('/backend/api.php?action=getBlogs');
          const url = baseUrl.includes('?') 
            ? `${baseUrl}&category=${slug}&limit=3`
            : `${baseUrl}?category=${slug}&limit=3`;
          const res = await fetch(url);
          const txt = await res.text();
          let data; 
          try { 
            data = JSON.parse(txt); 
          } catch (e) { 
            data = { status: 'error', blogs: [] }; 
          }
          
          // Handle different response formats (same as BlogPage.jsx)
          list = Array.isArray(data) ? data : (data.blogs || data.data || []);
          
          if (list && list.length > 0) {
            break;
          }
        }
        
        // Fallback: fetch all then client-side filter if empty
        if (!list || list.length === 0) {
          const allRes = await fetch(apiUrl('/backend/api.php?action=getBlogs'));
          const allTxt = await allRes.text();
          let allData; 
          try { 
            allData = JSON.parse(allTxt); 
          } catch (e) { 
            allData = { status: 'error', blogs: [] }; 
          }
          
          // Handle different response formats (same as BlogPage.jsx)
          const allList = Array.isArray(allData) ? allData : (allData.blogs || allData.data || []);
          
          list = allList.filter(b => {
            // Check category field (slug)
            const categorySlug = b.category || '';
            // Check category_name field
            const categoryName = b.category_name || '';
            
            return matchesReklam(categorySlug) || matchesReklam(categoryName);
          }).slice(0, 3);
        }
        
        const filtered = list
          .filter(b => b.is_published === 1 || b.is_published === true || b.is_published === '1' || b.is_published === undefined)
          .sort((a,b) => {
            const dateA = new Date(a.published_at || a.created_at || a.createdAt || 0);
            const dateB = new Date(b.published_at || b.created_at || b.createdAt || 0);
            return dateB - dateA || (b.id||0) - (a.id||0);
          })
          .slice(0, 3);
        
        setBlogs(filtered);
      } catch (e) {
        console.error('Error fetching blogs:', e);
        setBlogs([]);
      }
    };
    
    fetchBlogs();

    // Fetch FAQs from "reklam" category
    fetch(apiUrl('/backend/api.php?action=getFaq'))
      .then((res) => res.json())
      .then((data) => {
        const categories = data.categories || [];
        const questions = data.questions || [];
        const reklamCat = categories.find(c => {
          const title = String(c.title || '').toLowerCase();
          const slug = String(c.slug || '').toLowerCase();
          return title === 'reklam' || slug === 'reklam' || title.includes('reklam');
        });
        if (reklamCat) {
          const filtered = questions
            .filter(q => q.category_id === reklamCat.id)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .slice(0, 5);
          setFaqs(filtered);
        }
      })
      .catch((err) => console.error('Error fetching FAQs:', err));
  }, []);

  const whatsappNumber = '905324404011';
  const whatsappMessage = encodeURIComponent('Merhaba, reklam yönetimi hizmetleriniz hakkında bilgi almak istiyorum.');
  const whatsappLink = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${whatsappMessage}`;

  if (loading || !pageContent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-rose-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const additionalSections = pageContent.additional_sections || {};
  const heroStats = Array.isArray(additionalSections.hero_stats) ? additionalSections.hero_stats : [];
  const channelsSection = additionalSections.channels_section || {};
  const channelHighlights = Array.isArray(additionalSections.channel_highlights) ? additionalSections.channel_highlights : [];
  const processSection = additionalSections.process_section || {};
  const processSteps = Array.isArray(additionalSections.process_steps) ? additionalSections.process_steps : [];
  const deliverablesSection = additionalSections.deliverables || {};
  const deliverables = Array.isArray(deliverablesSection.items) ? deliverablesSection.items : [];
  const caseStudy = additionalSections.case_study || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-rose-50">
      {/* Hero */}
      <section
        className="relative bg-gradient-to-br from-rose-600 via-rose-700 to-rose-900 py-20 overflow-hidden"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
            <div className="w-full lg:w-1/2 text-white text-center lg:text-left">
              <div className="inline-flex">
                <Badge className="bg-white/20 text-white uppercase tracking-widest mb-4">
                  {pageContent.hero_badge_text || 'Reklam Yönetimi'}
                </Badge>
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6">
                {pageContent.hero_title || 'Performans Odaklı Reklam Kampanyaları ile Büyümeyi Hızlandırın'}
              </h1>
              <p className="text-xl text-rose-100 max-w-2xl mx-auto lg:mx-0">
                {pageContent.hero_description || 'Google, Meta, LinkedIn ve TikTok kampanyalarınızı tek çatı altında yönetiyor, bütçe optimizasyonu ve kreatif testlerle ROAS\'i sürdürülebilir biçimde artırıyoruz.'}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" className="bg-white text-rose-700 hover:bg-rose-100 shadow-lg" asChild>
                  <a href="/iletisim" className="inline-flex items-center">
                    <Mail className="w-5 h-5 mr-2" />
                    İletişime Geç
                  </a>
                </Button>
                <Button
                  size="lg"
                  className="bg-rose-700 text-white hover:bg-rose-800 shadow-lg border-2 border-white/30"
                  asChild
                >
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    WhatsApp ile Görüş
                  </a>
                </Button>
              </div>
            </div>
            <div className="w-full lg:w-1/2 flex justify-center">
              <div className="relative w-full max-w-lg">
                <div className="absolute -inset-4 bg-white/10 blur-3xl rounded-full"></div>
                <MediaDisplay
                  src={pageContent.hero_image_url || 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=1100&q=80'}
                  alt="Reklam yönetimi ekibi"
                  className="relative w-full rounded-3xl shadow-2xl object-cover border border-white/10"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
          </div>

          {heroStats.length > 0 && (
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-white hidden md:grid">
              {heroStats.map((item, idx) => (
                <div key={idx} className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm p-5 text-center">
                  <div className="text-3xl font-extrabold">{item.value}</div>
                  <div className="mt-2 text-sm text-rose-100">{item.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing */}
      <section
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.1s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-rose-100 text-rose-700">Paketler</Badge>
            <h2 className="text-3xl font-bold text-gray-900 mt-4">İş hedeflerinize uygun reklam yönetimi paketleri</h2>
            <p className="text-lg text-gray-600 mt-2 max-w-3xl mx-auto">
              Her plan, ekibinizin yapısına ve büyüme hedeflerinize uygun şekilde özelleştirilebilir. İlk görüşmede mevcut durumu analiz ediyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.length > 0 ? packages.map((pkg) => {
              // Get icon component - case-insensitive and trim
              const iconName = (pkg.icon || 'Rocket').toString().trim();
              const Icon = packageIconMap[iconName] || 
                          packageIconMap[iconName.charAt(0).toUpperCase() + iconName.slice(1).toLowerCase()] || 
                          Rocket;
              
              // Parse features from JSON string if needed
              let featuresData = pkg.features;
              if (typeof featuresData === 'string') {
                try {
                  featuresData = JSON.parse(featuresData);
                } catch (e) {
                  console.error('Error parsing features for package:', pkg.id, e);
                  featuresData = [];
                }
              }
              
              const features = Array.isArray(featuresData)
                ? featuresData
                    .filter((f) => typeof f === 'object' && f.included)
                    .map((f) => f.name)
                : [];
              
              return (
                <Card
                  key={pkg.id}
                  className={`relative border-0 shadow-xl hover:shadow-2xl transition-all duration-300 ease-in-out ${pkg.popular === 1 ? 'border-2 border-rose-500' : ''}`}
                >
                  {pkg.popular === 1 && (
                    <span className="absolute -top-3 -right-3 bg-rose-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Popüler
                    </span>
                  )}
                  <CardHeader className="p-6 pb-4 flex flex-col items-center text-center">
                    <Icon className="w-12 h-12 text-rose-600 mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <p className="text-4xl font-extrabold text-rose-600 mb-2">
                      {pkg.price && pkg.price > 0
                        ? `₺${formatPriceWithoutCents(pkg.price)}+ / ay`
                        : 'Özel teklif'}
                    </p>
                    <p className="text-sm text-gray-600 mb-4">Her ay</p>
                    <p className="text-sm text-gray-600 mb-6">
                      {pkg.subtitle || pkg.description}
                    </p>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1">
                    <div className="space-y-2 flex-1">
                      {features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                      
                      {/* Comparison Features from Database */}
                      {comparisonData.categories && comparisonData.categories.length > 0 && (
                        <Accordion type="single" collapsible className="mt-4">
                          <AccordionItem value="comparison" className="border-0">
                            <AccordionTrigger className="text-sm font-semibold text-rose-600 hover:text-rose-700 py-2">
                              Tüm Özellikleri Gör
                            </AccordionTrigger>
                            <AccordionContent className="px-0 pb-0">
                              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                {comparisonData.categories.map((categoryItem) => {
                                  const categoryFeatures = categoryItem.features || [];
                                  const category = categoryItem.category || categoryItem;
                                  
                                  if (categoryFeatures.length === 0) return null;
                                  
                                  const categoryKey = `${pkg.id}-${category.id || categoryItem.id}`;
                                  const isExpanded = expandedCategories[categoryKey];
                                  
                                  return (
                                    <div key={category.id || categoryItem.id} className="border-b border-gray-100 pb-3 last:border-0">
                                      <button
                                        onClick={() => setExpandedCategories(prev => ({
                                          ...prev,
                                          [categoryKey]: !prev[categoryKey]
                                        }))}
                                        className="w-full flex items-center justify-between text-left mb-2"
                                      >
                                        <h4 className="text-xs font-bold text-gray-900">{category.name}</h4>
                                        {isExpanded ? (
                                          <ChevronUp className="w-4 h-4 text-gray-500" />
                                        ) : (
                                          <ChevronDown className="w-4 h-4 text-gray-500" />
                                        )}
                                      </button>
                                      {isExpanded && (
                                        <div className="space-y-2">
                                          {categoryFeatures.map((feature) => {
                                            const featureValue = getFeatureValue(pkg.id, feature.id);
                                            return (
                                              <div key={feature.id} className="flex items-center justify-between text-xs">
                                                <span className="text-gray-600 flex-1">{feature.name}</span>
                                                <div className="ml-2">
                                                  {renderFeatureValue(featureValue.value_type, featureValue.value_text)}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </div>
                    <div className="pt-5">
                      <Button
                        className="w-full bg-rose-600 hover:bg-rose-700 text-white shadow-lg"
                        onClick={() => {
                          const isSpecialOffer = !pkg.price || pkg.price <= 0;
                          if (isSpecialOffer) {
                            setSelectedPackage(pkg);
                            setOfferDialogOpen(true);
                          } else if (pkg.url) {
                            window.open(pkg.url, '_blank');
                          } else {
                            window.location.href = '/iletisim';
                          }
                        }}
                      >
                        {!pkg.price || pkg.price <= 0 ? 'Özel Teklif İste' : 'Paketi Satın Al'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            }) : (
              <div className="col-span-3 text-center py-8 text-gray-500">
                Henüz paket eklenmemiş.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Özel Teklif Formu - Reklam Paketleri */}
      <Dialog open={offerDialogOpen} onOpenChange={(open) => {
        setOfferDialogOpen(open);
        if (!open) {
          setSelectedPackage(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reklam Paketi için Özel Teklif İste</DialogTitle>
            <DialogDescription>
              {selectedPackage ? `${selectedPackage.name} paketi için iletişim bilgilerinizi bırakın, kısa sürede size dönüş yapalım.` : 'İletişim bilgilerinizi bırakın, kısa sürede size dönüş yapalım.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitOfferRequest} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ads-offer-name">Ad Soyad</Label>
                <Input
                  id="ads-offer-name"
                  value={offerForm.customer_name}
                  onChange={(e) => setOfferForm({ ...offerForm, customer_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="ads-offer-company">Firma Adı (opsiyonel)</Label>
                <Input
                  id="ads-offer-company"
                  value={offerForm.company_name}
                  onChange={(e) => setOfferForm({ ...offerForm, company_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ads-offer-email">E-posta</Label>
                <Input
                  id="ads-offer-email"
                  type="email"
                  value={offerForm.customer_email}
                  onChange={(e) => setOfferForm({ ...offerForm, customer_email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="ads-offer-phone">Telefon</Label>
                <Input
                  id="ads-offer-phone"
                  value={offerForm.customer_phone}
                  onChange={(e) => setOfferForm({ ...offerForm, customer_phone: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ads-offer-notes">Kısaca ihtiyaçlarınızı anlatın (opsiyonel)</Label>
              <Textarea
                id="ads-offer-notes"
                rows={3}
                value={offerForm.project_description}
                onChange={(e) => setOfferForm({ ...offerForm, project_description: e.target.value })}
                placeholder="Hedefleriniz, mevcut reklam durumunuz, bütçe aralığınız vb."
              />
            </div>
            <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOfferDialogOpen(false);
                  setSelectedPackage(null);
                }}
              >
                Vazgeç
              </Button>
              <Button
                type="submit"
                className="bg-rose-600 hover:bg-rose-700 text-white"
                disabled={offerSubmitting}
              >
                {offerSubmitting ? 'Gönderiliyor...' : offerSubmitted ? 'Gönderildi' : 'Teklif Talebi Gönder'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Channels */}
      <section
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.2s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="bg-rose-100 text-rose-700">
              {channelsSection.badge_text || 'Kanallar'}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {channelsSection.title || 'Tüm kanallarda ölçeklenebilir performans'}
            </h2>
            <p className="text-lg text-gray-600">
              {channelsSection.description || 'Bütçenizi akıllı bir şekilde kanal bazında dağıtarak ROAS\'i yükseltiyor, çok kanallı funnel ile müşteri yolculuğunu optimize ediyoruz.'}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {channelHighlights.map((item, idx) => (
              <Card key={idx} className="border border-rose-100 hover:border-rose-300 transition-shadow shadow-sm hover:shadow-xl">
                <CardContent className="p-6 space-y-4">
                  <div>{iconMap[item.icon] || iconMap.BarChart3}</div>
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section
        className="py-16"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.3s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12">
            <div className="w-full lg:w-2/5 flex justify-center hidden lg:flex">
              <div className="relative w-full max-w-sm">
                <div className="absolute -inset-4 bg-rose-200 blur-3xl rounded-full"></div>
                <MediaDisplay
                  src={processSection.image_url || 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1000&q=80'}
                  alt="Reklam optimizasyon süreci"
                  className="relative w-full rounded-3xl shadow-xl object-cover border border-rose-100"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
            <div className="w-full lg:w-3/5 text-center lg:text-left">
              {/* Mobile Image - Above Badge */}
              <div className="w-full flex justify-center mb-6 lg:hidden">
                <div className="relative w-full max-w-sm">
                  <div className="absolute -inset-4 bg-rose-200 blur-3xl rounded-full"></div>
                  <MediaDisplay
                    src={processSection.image_url || 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1000&q=80'}
                    alt="Reklam optimizasyon süreci"
                    className="relative w-full rounded-3xl shadow-xl object-cover border border-rose-100"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
              </div>
              <div className="mb-8 space-y-3">
                <div className="flex justify-center lg:inline-flex">
                  <Badge className="bg-rose-100 text-rose-700">
                    {processSection.badge_text || 'Çalışma Planı'}
                  </Badge>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {processSection.title || '4 adımda kampanya başarısı'}
                </h2>
                <p className="text-gray-600">
                  {processSection.description || 'Keşiften scale etmeye kadar tüm aşamaları şeffaf ve ölçülebilir şekilde yönetiyoruz.'}
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {processSteps.map((step, idx) => (
                  <Card key={idx} className="border border-rose-200 h-full">
                    <CardContent className="p-6 space-y-4">
                      <div className="inline-flex items-center px-3 py-1 bg-rose-50 text-rose-600 text-xs font-semibold rounded-full">
                        <CalendarDays className="w-3.5 h-3.5 mr-1" />
                        {step.phase}
                      </div>
                      <ul className="space-y-2 text-sm text-gray-600">
                        {(step.details || []).map((detail, didx) => (
                          <li key={didx} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-400"></span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.4s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
            <div className="w-full">
              <div className="relative w-full max-w-lg mx-auto lg:ml-auto">
                <div className="absolute -inset-4 bg-rose-100 blur-3xl rounded-full"></div>
                <MediaDisplay
                  src={caseStudy.image_url || 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80'}
                  alt="Reklam vaka çalışması"
                  className="relative w-full rounded-3xl shadow-xl object-cover border border-rose-100"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
            <div className="text-center lg:text-right space-y-6">
              <div className="inline-flex">
                <Badge className="bg-rose-100 text-rose-700">Vaka Çalışması</Badge>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                {caseStudy.brand || 'E-ticaret (Moda)'}
              </h2>
              <div className="space-y-4 text-gray-600">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1">Sorun</h3>
                  <p className="text-center lg:text-right">{caseStudy.challenge || ''}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1">Yaklaşımımız</h3>
                  <p className="text-center lg:text-right">{caseStudy.solution || ''}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {(caseStudy.results || []).map((item, idx) => (
                  <div key={idx} className="rounded-2xl bg-white shadow-md border border-rose-100 p-6 text-center">
                    <div className="text-2xl font-bold text-rose-600">{item.metric}</div>
                    <p className="mt-2 text-sm text-gray-600">{item.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex justify-center lg:justify-end">
                <Button
                  variant="outline"
                  className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  onClick={() => window.open('/blog/performance-ads-case-study', '_blank')}
                >
                  Detaylı Vaka İncelemesi
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section
        className="py-16 bg-rose-900"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.5s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-white/20 text-white">
              {deliverablesSection.badge_text || 'Teslimatlar'}
            </Badge>
            <h2 className="text-3xl font-bold mt-4">
              {deliverablesSection.title || 'Reklam yönetimi sürecinde neler sunuyoruz?'}
            </h2>
            <p className="text-rose-100 mt-3">
              {deliverablesSection.description || 'Kampanya kurulumundan raporlamaya kadar tüm çıktıları dokümante ediyor ve sürekli iyileştirme yapıyoruz.'}
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {deliverables.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-white mt-0.5" />
                <span className="text-sm text-rose-50 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section
        className="py-16 bg-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.6s' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
            <div>
              <Badge className="bg-rose-100 text-rose-700">Blog</Badge>
              <h2 className="text-3xl font-bold text-gray-900 mt-3">Reklam ve Pazarlamaya Dair İçerikler</h2>
              <p className="text-gray-600 mt-2 max-w-3xl">
                Reklam stratejileri, kampanya optimizasyonları ve performans pazarlaması üzerine rehberler.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-rose-200 text-rose-600 hover:bg-rose-50"
              onClick={() => window.open('/blog?category=reklam', '_blank')}
            >
              Tüm Yazıları Gör
            </Button>
          </div>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {blogs.length > 0 ? blogs.map((article, idx) => (
              <Card key={idx} className="overflow-hidden border-rose-100 hover:shadow-lg transition-shadow">
                <div className="aspect-[16/9] bg-rose-50 overflow-hidden">
                  <MediaDisplay
                    src={article.image || article.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80'}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    videoProps={{ controls: true }}
                    imgProps={{ loading: "lazy" }}
                  />
                </div>
                <CardContent className="p-5 space-y-3">
                  <div className="inline-flex items-center px-3 py-1 bg-rose-50 text-rose-600 text-xs font-semibold rounded-full">
                    <BookOpen className="w-3.5 h-3.5 mr-1" />
                    {article.category_name || article.category || 'Reklam'}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{article.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{article.excerpt || article.summary || ''}</p>
                  <button
                    onClick={() => window.open(`/blog/${article.slug}`, '_self')}
                    className="inline-flex items-center text-sm font-semibold text-rose-600 hover:text-rose-700"
                  >
                    Yazıyı Oku
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </CardContent>
              </Card>
            )) : (
              <div className="col-span-3 text-center py-8 text-gray-500">
                Henüz blog yazısı eklenmemiş.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section
        className="py-16 bg-rose-50"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.7s' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <Badge className="bg-rose-200 text-rose-800">Sıkça Sorulan Sorular</Badge>
            <h2 className="text-3xl font-bold text-gray-900 mt-3">Reklam yönetimi hakkında merak edilenler</h2>
            <p className="text-gray-600 mt-2">Kampanya süreci, bütçe ve raporlama ile ilgili sık sorulan soruları yanıtladık.</p>
          </div>
          <div className="space-y-4">
            {faqs.length > 0 ? faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl bg-white border border-rose-100 px-6 py-5 shadow-sm"
                open={index === 0}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <div className="flex items-center text-left">
                    <HelpCircle className="w-5 h-5 text-rose-500 mr-3" />
                    <span className="text-base font-semibold text-gray-900 group-open:text-rose-600">
                      {faq.question}
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-rose-400 transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-gray-600">
                  {faq.answer}
                </p>
              </details>
            )) : (
              <div className="text-center py-8 text-gray-500">
                Henüz soru eklenmemiş.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 bg-gradient-to-r from-rose-600 to-rose-700 text-white"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.6s ease-in 0.8s' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">Reklam Bütçenizi Birlikte Ölçekleyelim</h2>
            <p className="text-xl text-rose-100 mb-8 max-w-3xl mx-auto">
              Ücretsiz hesap denetimi ve fırsat analizi ile başlayalım, 90 günlük büyüme planını birlikte oluşturalım. Kampanyalarınızı tek çatı altında yönetelim.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/iletisim"
                className="inline-flex items-center px-8 py-4 bg-white text-rose-700 rounded-lg hover:bg-rose-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                Ücretsiz Kampanya Denetimi
              </a>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                WhatsApp ile Görüş
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <BarChart3 className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Ücretsiz Hesap Analizi</h3>
                <p className="text-rose-100">Mevcut kampanyalarınızın performans raporu ve fırsat alanları</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Zap className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Haftalık Performans Raporu</h3>
                <p className="text-rose-100">KPI takibi, bütçe optimizasyonu ve aksiyon önerileri</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Target className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Strateji Atölyesi</h3>
                <p className="text-rose-100">Aylık kampanya değerlendirmesi ve büyüme planı güncellemesi</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
